import { useEffect, useMemo, useState } from 'react';
import { EM_DASH, HFBox, HFButton, HFCard, HFChip, HFDataTable, HFTooltip, useHfTheme, type HFGridColDef } from '@hf/designSystem/v1';
import {
  omsLimitFigures,
  omsPositionListSchema,
  omsPostTradeLimitsSchema,
  type OmsLimitRow,
  type OmsPosition,
  type OmsPositionList,
  type OmsPostTradeLimits,
} from '@hf/sharedContract';
import { useHttp } from '@core/utils/http';
import { TableSkeleton } from './TableSkeleton';

type LimitGridRow = {
  id: string;
  contract: string;
  closing: number | null;
  working: number | null;
  closePlusOrders: number | null;
  softLimit: number | null;
  hardLimit: number | null;
  capacity: string;
  spreadNet: number | null;
  overSoft: boolean;
  overHard: boolean;
  status: string;
};

const LIMITS_NOTICE =
  'Soft and hard limits come from the Red Flags layout in HedgeFacts Applications. On-prem does not return a layout yet, so limits and capacity stay blank and no row is flagged.';
const SPREAD_NOTE = 'Spread net comes from HedgeFacts Applications. The Cloud does not add up spread legs.';

function LimitStatusChip({ hard, label }: { hard: boolean; label: string }) {
  const theme = useHfTheme();
  const palette = hard ? theme.palette.error : theme.palette.warning;
  return (
    <HFChip
      tone={hard ? 'block' : 'warn'}
      label={label}
      sx={{
        height: 24,
        fontWeight: 800,
        bgcolor: palette.main,
        color: palette.contrastText,
        border: 0,
        '& .MuiChip-label': { color: 'inherit', px: 1, py: 0 },
      }}
    />
  );
}

function textOrDash(value: number | null): string {
  return value == null ? EM_DASH : String(value);
}

function Qty({ value }: { value: number | null }) {
  const text = textOrDash(value);
  const color = text === EM_DASH ? 'text.secondary' : value != null && value < 0 ? 'error.main' : 'text.primary';
  return (
    <HFBox component="span" sx={{ color }}>
      {text}
    </HFBox>
  );
}

function capacityLabel(closePlusOrders: number | null, softLimit: number | null, hardLimit: number | null): string {
  const figures = omsLimitFigures(closePlusOrders, softLimit, hardLimit);
  if (figures.overSoftLimit) return figures.capacityToHard == null ? '' : `${figures.capacityToHard} to hard`;
  if (figures.capacityToSoft != null) return `${figures.capacityToSoft} to soft`;
  return '';
}

function toRow(position: OmsPosition | null, limit: OmsLimitRow | undefined): LimitGridRow {
  const closePlusOrders = position?.closePlusOrders ?? null;
  const softLimit = limit?.softLimit ?? null;
  const hardLimit = limit?.hardLimit ?? null;
  const figures = omsLimitFigures(closePlusOrders, softLimit, hardLimit);
  const overHard = figures.capacityToHard != null && figures.capacityToHard < 0;
  const overSoft = figures.overSoftLimit && !overHard;
  return {
    id: position?.positionKey ?? limit?.positionKey ?? '',
    contract: position?.bbFullSymbol || limit?.positionKey || '',
    closing: position?.closing ?? null,
    working: position?.working ?? null,
    closePlusOrders,
    softLimit,
    hardLimit,
    capacity: capacityLabel(closePlusOrders, softLimit, hardLimit),
    spreadNet: limit?.spreadNet ?? null,
    overSoft,
    overHard,
    status: overHard ? 'Over hard limit' : overSoft ? 'Over soft limit' : '',
  };
}

function gridRows(positions: readonly OmsPosition[], limits: readonly OmsLimitRow[]): LimitGridRow[] {
  const limitsByKey = new Map(limits.map((row) => [row.positionKey, row]));
  const seen = new Set<string>();
  const rows = positions.map((position) => {
    seen.add(position.positionKey);
    return toRow(position, limitsByKey.get(position.positionKey));
  });
  for (const limit of limits) {
    if (!seen.has(limit.positionKey)) rows.push(toRow(null, limit));
  }
  return rows;
}

const COLUMNS: Array<HFGridColDef<LimitGridRow>> = [
  { field: 'contract', headerName: 'Contract', width: 170, sortable: false },
  { field: 'closing', headerName: 'Closing', width: 110, sortable: false, renderCell: ({ row }) => <Qty value={row.closing} /> },
  { field: 'working', headerName: 'Working', width: 110, sortable: false, renderCell: ({ row }) => <Qty value={row.working} /> },
  {
    field: 'closePlusOrders',
    headerName: 'Close+Orders',
    width: 140,
    sortable: false,
    renderCell: ({ row }) => <Qty value={row.closePlusOrders} />,
  },
  { field: 'softLimit', headerName: 'Soft limit', width: 110, sortable: false, renderCell: ({ row }) => textOrDash(row.softLimit) },
  { field: 'hardLimit', headerName: 'Hard limit', width: 110, sortable: false, renderCell: ({ row }) => textOrDash(row.hardLimit) },
  { field: 'capacity', headerName: 'Capacity left', width: 140, sortable: false },
  { field: 'spreadNet', headerName: 'Spread net', width: 120, sortable: false, renderCell: ({ row }) => <Qty value={row.spreadNet} /> },
  {
    field: 'status',
    headerName: 'Status',
    width: 150,
    sortable: false,
    renderCell: ({ row }) => (row.status ? <LimitStatusChip hard={row.overHard} label={row.status} /> : null),
  },
];

/** Closing, Working and Close+Orders from positions, joined to limits by position key. Limits stay blank until on-prem sends them. */
export function PositionLimitsGrid({ date }: { date?: string }) {
  const { request } = useHttp();
  const [positions, setPositions] = useState<OmsPosition[] | null>(null);
  const [limits, setLimits] = useState<OmsLimitRow[] | null>(null);
  const [positionsFailed, setPositionsFailed] = useState(false);
  const [limitsFailed, setLimitsFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPositions(null);
    setLimits(null);
    setPositionsFailed(false);
    setLimitsFailed(false);
    const positionsPath = date ? `/oms/positions?date=${encodeURIComponent(date)}` : '/oms/positions';
    void Promise.all([
      request<OmsPositionList>(positionsPath, { responseSchema: omsPositionListSchema }),
      request<OmsPostTradeLimits>('/oms/post-trade/limits', { responseSchema: omsPostTradeLimitsSchema }),
    ]).then(([positionResult, limitResult]) => {
      if (cancelled) return;
      if (positionResult.status === 200 && positionResult.data) setPositions(positionResult.data.positions);
      else setPositionsFailed(true);
      if (limitResult.status === 200 && limitResult.data) setLimits(limitResult.data.rows);
      else setLimitsFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [date, request]);

  const settled = (positions !== null || positionsFailed) && (limits !== null || limitsFailed);
  const rows = useMemo(() => gridRows(positions ?? [], limits ?? []), [positions, limits]);
  const emptyMessage = positionsFailed ? 'Position limits unavailable.' : 'No positions for this trade day.';

  return (
    <HFCard component="section" aria-label="Limits by position" aria-busy={!settled}>
      <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <HFBox sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3, minHeight: 40 }}>
          <HFBox component="h2" sx={{ m: 0, fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: '24px' }}>
            Limits by position
          </HFBox>
          <HFTooltip title="Applying a limit layout comes in a later phase.">
            <HFBox component="span" sx={{ display: 'inline-flex' }}>
              <HFButton size="small" variant="outlined" disabled>
                Apply layout
              </HFButton>
            </HFBox>
          </HFTooltip>
        </HFBox>
        {settled && limitsFailed ? (
          <HFBox component="p" sx={{ m: 0, fontSize: 13, color: 'error.main', lineHeight: 1.5 }}>
            Position limits unavailable.
          </HFBox>
        ) : settled && !positionsFailed && limits?.length === 0 ? (
          <HFBox component="p" sx={{ m: 0, fontSize: 13, color: 'text.secondary', lineHeight: 1.5 }}>
            {LIMITS_NOTICE}
          </HFBox>
        ) : null}
        {settled ? (
          <HFDataTable<LimitGridRow>
            rows={rows}
            columns={COLUMNS}
            getRowId={(row) => row.id}
            columnSearch={false}
            disableColumnMenu
            hideFooter
            autoHeight
            disableColumnVirtualization
            slots={{ noRowsOverlay: () => <HFBox sx={{ px: 2, py: 3, color: 'text.secondary', fontSize: 14 }}>{emptyMessage}</HFBox> }}
          />
        ) : (
          <TableSkeleton testId="limits-skeleton" />
        )}
        <HFBox component="p" sx={{ m: 0, fontSize: 13, color: 'text.secondary' }}>
          {SPREAD_NOTE}
        </HFBox>
      </HFBox>
    </HFCard>
  );
}
